import { useMStockMarketCapQuery } from '@/queries/mStock/mStockQueries'
import { useCallback, useMemo } from 'react'
import { useCoinMeta } from './useCoinMeta.js'

export const MARKET_CAP_POLLING_PERIOD = '5m-50s'

export const useMarketCapRanking = () => {
  const {
    data: rankData,
    isLoading: isMarketCapLoading,
    isError: isMarketCapError,
    refetch: refetchMarketCap
  } = useMStockMarketCapQuery()

  const marketCapItems = useMemo(() => {
    return Array.isArray(rankData?.marketCap)
      ? rankData.marketCap
      : Object.values(rankData?.marketCap ?? {})
  }, [rankData?.marketCap])

  const marketCapAssetIds = useMemo(() => {
    return marketCapItems.map((marketCapItem) => marketCapItem.assetId)
  }, [marketCapItems])

  const {
    coinMetaMap,
    missingAssetIds,
    hasCompletedRetry,
    isLoading: isCoinInfoLoading,
    isError: isCoinInfoError,
    refetch: refetchCoinInfo
  } = useCoinMeta({
    assetIds: marketCapAssetIds,
    enabled: !isMarketCapLoading
  })

  const data = useMemo(() => {
    return marketCapItems.map((marketCapItem) => {
      const assetId = String(marketCapItem.assetId)
      const coinInfo = coinMetaMap[assetId]

      if (coinInfo) {
        return {
          ...coinInfo,
          ...marketCapItem,
          assetId,
          metaStatus: 'ready'
        }
      }

      return {
        ...marketCapItem,
        assetId,
        metaStatus: hasCompletedRetry || isCoinInfoError ? 'fallback' : 'loading'
      }
    })
  }, [coinMetaMap, hasCompletedRetry, isCoinInfoError, marketCapItems])

  const refetchAll = useCallback(() => {
    return Promise.all([refetchCoinInfo(), refetchMarketCap()])
  }, [refetchCoinInfo, refetchMarketCap])

  const hasMarketCapData = marketCapItems.length > 0
  const hasMissingMeta = missingAssetIds.length > 0

  const isMetaSettled =
    !isCoinInfoLoading && (!hasMissingMeta || hasCompletedRetry || isCoinInfoError)

  const isInitialLoading = isMarketCapLoading || (hasMarketCapData && !isMetaSettled)

  const isInitialError = isMarketCapError && !hasMarketCapData

  return {
    data,
    lastFetchTime: rankData?.lastFetchTime ?? null,
    missingAssetIds,
    isLoading: isInitialLoading,
    isError: isInitialError,
    // 사용자 재조회용
    refetchAll,
    // 자동 polling 용
    refetchMarketCap,
    refetchCoinInfo
  }
}
